import { supabase } from '@/integrations/supabase/client';
import { safeStorage, getSimpleFingerprint } from '@/utils/safariStorage';

export interface PropertyView {
  id: string;
  property_id: string;
  buyer_id: string | null;
  viewer_ip: string | null;
  user_agent: string | null;
  viewed_at: string;
  created_at: string;
}

// Helper function to get a browser fingerprint with Safari fallback
const getBrowserFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Safari 17+ may throw or return noise in private mode
    if (!ctx) {
      return getSimpleFingerprint();
    }
    
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Browser fingerprint', 2, 2);
    
    const canvasData = canvas.toDataURL();
    
    // Check for Safari noise (empty or very short data URL)
    if (!canvasData || canvasData.length < 100) {
      return getSimpleFingerprint();
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvasData
    ].join('|');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  } catch (error) {
    // Canvas fingerprinting failed (Safari Private Mode, etc.)
    console.warn('Canvas fingerprinting failed, using simple fallback');
    return getSimpleFingerprint();
  }
};

// In-memory fallback for session ID
let memorySessionId: string | null = null;

// Helper function to get or generate session ID with Safari-safe storage
const getSessionId = (): string => {
  try {
    let sessionId = safeStorage.getItem('godyspo_session_id');
    if (!sessionId) {
      if (memorySessionId) {
        return memorySessionId;
      }
      sessionId = crypto.randomUUID();
      safeStorage.setItem('godyspo_session_id', sessionId);
      memorySessionId = sessionId;
    }
    return sessionId;
  } catch (error) {
    console.warn('Error accessing session storage:', error);
    if (!memorySessionId) {
      memorySessionId = crypto.randomUUID();
    }
    return memorySessionId;
  }
};

export const propertyViewsService = {
  // Track a property view with enhanced deduplication
  async trackView(propertyId: string, buyerId?: string): Promise<void> {
    try {
      const userAgent = navigator.userAgent;
      const sessionId = getSessionId();
      const fingerprint = getBrowserFingerprint();
      
      // Define deduplication window based on user type
      const deduplicationHours = buyerId ? 1 : 24; // 1 hour for authenticated, 24 hours for anonymous
      const deduplicationTime = new Date(Date.now() - deduplicationHours * 60 * 60 * 1000).toISOString();
      
      // Check for recent views with enhanced criteria
      let recentViewsQuery = supabase
        .from('property_views')
        .select('id')
        .eq('property_id', propertyId)
        .gte('viewed_at', deduplicationTime);

      // For authenticated users, check by buyer_id
      if (buyerId) {
        recentViewsQuery = recentViewsQuery.eq('buyer_id', buyerId);
      } else {
        // For anonymous users, check by user_agent (as a proxy for session)
        // In a real implementation, you'd want to capture IP on the backend
        recentViewsQuery = recentViewsQuery
          .eq('user_agent', userAgent)
          .is('buyer_id', null);
      }

      const { data: recentViews } = await recentViewsQuery.limit(1);

      // Only track if no recent view found
      if (!recentViews || recentViews.length === 0) {
        // Additional rate limiting: check for excessive views in the last hour
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
        const { count: recentCount } = await supabase
          .from('property_views')
          .select('*', { count: 'exact', head: true })
          .eq('property_id', propertyId)
          .eq('user_agent', userAgent)
          .gte('viewed_at', oneHourAgo);

        // Allow max 3 views per property per hour from same user agent
        if ((recentCount || 0) < 3) {
          const { error } = await supabase
            .from('property_views')
            .insert({
              property_id: propertyId,
              buyer_id: buyerId || null,
              user_agent: userAgent,
              viewed_at: new Date().toISOString()
            });

          if (error) {
            console.error('Error tracking property view:', error);
          }
        } else {
          console.log('Rate limit exceeded for property view tracking');
        }
      }
    } catch (error) {
      console.error('Error tracking property view:', error);
      // Don't throw error - view tracking shouldn't break the app
    }
  },

  // Get unique view count for a specific property
  async getPropertyViewCount(propertyId: string): Promise<number> {
    try {
      // Get unique views by counting distinct user_agent + buyer_id combinations
      const { data, error } = await supabase
        .from('property_views')
        .select('user_agent, buyer_id')
        .eq('property_id', propertyId);

      if (error) {
        console.error('Error fetching property view count:', error);
        return 0;
      }

      if (!data) return 0;

      // Create unique identifiers for deduplication
      const uniqueViews = new Set();
      data.forEach(view => {
        const identifier = view.buyer_id || view.user_agent || 'anonymous';
        uniqueViews.add(identifier);
      });

      return uniqueViews.size;
    } catch (error) {
      console.error('Error fetching property view count:', error);
      return 0;
    }
  },

  // Get total unique view count for all properties owned by a user
  async getUserTotalViews(userId: string): Promise<number> {
    try {
      // First, get all property IDs for this user
      const { data: userProperties, error: propertiesError } = await supabase
        .from('properties')
        .select('id')
        .eq('seller_id', userId);

      if (propertiesError) {
        console.error('Error fetching user properties:', propertiesError);
        return 0;
      }

      if (!userProperties || userProperties.length === 0) {
        return 0;
      }

      // Extract property IDs into an array
      const propertyIds = userProperties.map(property => property.id);

      // Get all views for these properties
      const { data: views, error } = await supabase
        .from('property_views')
        .select('property_id, user_agent, buyer_id')
        .in('property_id', propertyIds);

      if (error) {
        console.error('Error fetching user total views:', error);
        return 0;
      }

      if (!views) return 0;

      // Create unique identifiers across all properties
      const uniqueViews = new Set();
      views.forEach(view => {
        const identifier = `${view.property_id}-${view.buyer_id || view.user_agent || 'anonymous'}`;
        uniqueViews.add(identifier);
      });

      return uniqueViews.size;
    } catch (error) {
      console.error('Error fetching user total views:', error);
      return 0;
    }
  },

  // Get view analytics for properties owned by a user with enhanced metrics
  async getUserPropertyViews(userId: string): Promise<Array<{property_id: string, unique_views: number, total_views: number}>> {
    try {
      const { data: userProperties, error: propertiesError } = await supabase
        .from('properties')
        .select('id')
        .eq('seller_id', userId);

      if (propertiesError) {
        console.error('Error fetching user properties:', propertiesError);
        return [];
      }

      if (!userProperties || userProperties.length === 0) {
        return [];
      }

      const propertyIds = userProperties.map(property => property.id);

      const { data, error } = await supabase
        .from('property_views')
        .select('property_id, user_agent, buyer_id')
        .in('property_id', propertyIds);

      if (error) {
        console.error('Error fetching user property views:', error);
        return [];
      }

      if (!data) return [];

      // Group by property_id and calculate both unique and total views
      const propertyStats: Record<string, {unique: Set<string>, total: number}> = {};
      
      data.forEach(view => {
        if (!propertyStats[view.property_id]) {
          propertyStats[view.property_id] = {
            unique: new Set(),
            total: 0
          };
        }
        
        const identifier = view.buyer_id || view.user_agent || 'anonymous';
        propertyStats[view.property_id].unique.add(identifier);
        propertyStats[view.property_id].total++;
      });

      return Object.entries(propertyStats).map(([property_id, stats]) => ({
        property_id,
        unique_views: stats.unique.size,
        total_views: stats.total
      }));
    } catch (error) {
      console.error('Error fetching user property views:', error);
      return [];
    }
  },

  // Clean up obvious test data (optional method for admin use)
  async cleanTestData(): Promise<void> {
    try {
      // Remove views that are obviously from testing (same user agent with excessive views)
      const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      
      // This would be used by administrators to clean up test data
      console.log('Test data cleanup would be performed here with proper admin authentication');
    } catch (error) {
      console.error('Error cleaning test data:', error);
    }
  }
};
